import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Store as StoreIcon,
  Activity,
  CheckCircle2,
  AlertTriangle,
  FileText,
  MoreVertical,
  Clock,
  Upload,
  BarChart3,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";

const TYPE_LABEL = {
  type1: "Type 1 · TD",
  type2: "Type 2 · Mall Hourly",
  type3: "Type 3 · DOS 8.3",
  type4: "Type 4 · Subway H",
  type5: "Type 5 · Mall Hourly2",
};

export default function Dashboard() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const isAdmin = user?.group === "admin";
  const localeForDate = i18n.language === "zh" ? "zh-CN" : i18n.language === "ms" ? "ms-MY" : "en-GB";

  useEffect(() => {
    Promise.all([api.get("/dashboard/stats"), api.get("/stores")])
      .then(([s, st]) => {
        setStats(s.data);
        setStores(st.data);
      })
      .catch(() => toast.error(t("dashboard.loadFailed")))
      .finally(() => setLoading(false));
  }, [t]);

  const toggleStoreActive = async (s) => {
    try {
      await api.patch(`/stores/${s.id}`, { active: !s.active });
      setStores((prev) =>
        prev.map((x) => (x.id === s.id ? { ...x, active: !x.active } : x))
      );
      toast.success(!s.active ? t("dashboard.storeEnabled") : t("dashboard.storeDisabled"));
    } catch (e) {
      toast.error(t("dashboard.loadFailed"));
    }
  };

  const toggleStoreUpload = async (s) => {
    const nextValue = s.upload_enabled === false;
    try {
      await api.patch(`/stores/${s.id}`, { upload_enabled: nextValue });
      setStores((prev) =>
        prev.map((x) => (x.id === s.id ? { ...x, upload_enabled: nextValue } : x))
      );
      toast.success(
        nextValue ? t("dashboard.uploadEnabled") : t("dashboard.uploadDisabled")
      );
    } catch (e) {
      toast.error(t("dashboard.loadFailed"));
    }
  };

  return (
    <div data-testid="dashboard-page">
      <div className="flex items-end justify-between mb-7 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.32em] text-slate-400 mb-1">
            {t("dashboard.breadcrumb")}
          </div>
          <h1 className="text-3xl font-extrabold text-slate-900">{t("dashboard.title")}</h1>
        </div>
        {isAdmin && (
          <Link
            to="/stores/new"
            className="px-4 py-2.5 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-semibold text-sm transition flex items-center gap-2"
            data-testid="add-store-btn"
          >
            {t("dashboard.newStore")}
          </Link>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          icon={StoreIcon}
          label={t("dashboard.stats.totalStores")}
          value={stats?.stores_count ?? "—"}
          tone="blue"
          testid="stat-stores"
        />
        <StatCard
          icon={Activity}
          label={t("dashboard.stats.activeStores")}
          value={stats?.active_stores ?? "—"}
          tone="green"
          testid="stat-active"
        />
        <StatCard
          icon={CheckCircle2}
          label={t("dashboard.stats.filesUploaded")}
          value={stats?.files_uploaded ?? "—"}
          tone="cyan"
          testid="stat-uploaded"
        />
        <Link to="/errors" data-testid="stat-errors-link" className="block">
          <StatCard
            icon={AlertTriangle}
            label={t("dashboard.stats.errors")}
            value={stats?.files_errors ?? "—"}
            tone="amber"
            testid="stat-errors"
            clickable
          />
        </Link>
      </div>

      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-slate-900">{t("dashboard.storesList")}</h2>
        <div className="text-sm text-slate-500">{t("dashboard.storesCount", { count: stores.length })}</div>
      </div>

      {loading ? (
        <div className="pg-card p-12 text-center text-slate-400">{t("dashboard.loadingStores")}</div>
      ) : stores.length === 0 ? (
        <div className="pg-card p-12 text-center dot-grid">
          <StoreIcon className="mx-auto text-slate-300 mb-3" size={42} />
          <p className="text-slate-500 mb-4">{t("dashboard.noStores")}</p>
          {isAdmin && (
            <Link
              to="/stores/new"
              className="text-cyan-600 font-semibold text-sm hover:underline"
            >
              {t("dashboard.createFirst")}
            </Link>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {stores.map((s, i) => (
            <div
              key={s.id}
              className={`pg-card ${s.active ? "pg-card-active" : "pg-card-inactive"} anim-in`}
              style={{ animationDelay: `${i * 0.04}s`, opacity: 0 }}
              data-testid={`store-card-${s.id}`}
            >
              <div className="p-5">
                <div className="flex items-start justify-between gap-2 mb-4">
                  <Link
                    to={`/stores/${s.id}/files`}
                    className="text-[15px] font-bold text-blue-700 hover:text-cyan-600 leading-snug"
                  >
                    {s.name}
                  </Link>
                  {isAdmin && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          className="text-slate-400 hover:text-slate-700 p-1 -mt-1"
                          data-testid={`store-menu-${s.id}`}
                          aria-label="Store menu"
                        >
                          <MoreVertical size={16} />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-44">
                        <DropdownMenuItem asChild>
                          <Link to={`/stores/${s.id}/files`}>{t("dashboard.uploadedFiles")}</Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to={`/stores/${s.id}/edit`}>{t("dashboard.editStore")}</Link>
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className={s.active ? "text-rose-600" : "text-emerald-600"}
                          onClick={() => toggleStoreActive(s)}
                        >
                          {s.active ? t("dashboard.disableAccess") : t("dashboard.enableAccess")}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className={s.upload_enabled === false ? "text-emerald-600" : "text-amber-700"}
                          onClick={() => toggleStoreUpload(s)}
                        >
                          {s.upload_enabled === false
                            ? t("dashboard.enableUpload")
                            : t("dashboard.disableUpload")}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>

                <div className="space-y-2.5 text-sm">
                  <Row icon={Clock} label={t("dashboard.lastUpload")}>
                    <span className="font-medium text-slate-700">
                      {s.last_upload_at
                        ? new Date(s.last_upload_at).toLocaleDateString(localeForDate, {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })
                        : "—"}
                    </span>
                  </Row>
                  <Row icon={Upload} label={t("dashboard.filesUploaded")}>
                    <span className="stat-num text-base">{s.files_count}</span>
                  </Row>
                  <Row icon={FileText} label={t("dashboard.storeType")}>
                    <span className="pill pill-info">{TYPE_LABEL[s.store_type] || s.store_type}</span>
                  </Row>
                  <Row icon={Activity} label={t("common.status")}>
                    <span className={
                      !s.active
                        ? "pill pill-danger"
                        : s.upload_enabled === false
                          ? "pill pill-warning"
                          : "pill pill-success"
                    }>
                      {!s.active
                        ? t("common.inactive")
                        : s.upload_enabled === false
                          ? t("dashboard.uploadDisabledStatus")
                          : t("common.active")}
                    </span>
                  </Row>
                </div>
              </div>
              <div className="px-5 pb-4 border-t border-slate-100 pt-3 flex items-center justify-between text-xs">
                <span className="text-slate-400 uppercase tracking-wider">
                  MID · {s.machine_id}
                </span>
                <div className="flex items-center gap-3 font-semibold">
                  <Link to={`/stores/${s.id}/data-analysis`} className="text-blue-600 hover:text-blue-700 flex items-center gap-1">
                    <BarChart3 size={13} /> {t("dataAnalysis.title")}
                  </Link>
                  <Link to={`/stores/${s.id}/files`} className="text-cyan-600 hover:text-cyan-700">
                    {t("dashboard.viewFiles")}
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Row({ icon: Icon, label, children }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="flex items-center gap-2 text-slate-500 text-[13px]">
        <Icon size={14} />
        {label}
      </span>
      <div className="flex-1 border-b border-dashed border-slate-200 mx-1" />
      {children}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, tone, testid, clickable }) {
  const tones = {
    blue: "from-blue-500/15 to-cyan-500/10 text-blue-700 border-blue-100",
    green: "from-emerald-500/15 to-teal-500/10 text-emerald-700 border-emerald-100",
    cyan: "from-cyan-400/15 to-sky-400/10 text-cyan-700 border-cyan-100",
    amber: "from-amber-500/15 to-orange-500/10 text-amber-700 border-amber-100",
  };
  const interactive = clickable
    ? "cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-transform duration-200"
    : "";
  return (
    <div className={`pg-card p-5 bg-gradient-to-br ${tones[tone]} ${interactive}`} data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-2 font-semibold">
            {label}
          </div>
          <div className="stat-num text-3xl">{value}</div>
        </div>
        <div className="w-10 h-10 rounded-lg bg-white grid place-items-center shadow-sm">
          <Icon size={18} />
        </div>
      </div>
    </div>
  );
}
